import { checkPermission } from "../Configurations/PermissionConfigrations";
import { isStudentUser } from "./isStudentUser";
import { resolveUserRoleLabel } from "./resolveUserRoleLabel";

/** Self-scoped lists a trainer sees in the portal. */
export const TRAINER_PORTAL_PERMISSIONS = [
  "VIEW_SELF_BATCHES",
  "VIEW_SELF_BATCH_STUDENTS",
  "VIEW_SELF_BATCH_CLASSES",
  "VIEW_SELF_BATCH_CLASS_RECORDS",
  "VIEW_SELF_CLASS_ATTENDANCE",
  "VIEW_SELF_ASSESSMENTS",
  "VIEW_SELF_ASSESSMENT_TESTS",
  "VIEW_SELF_ASSESSMENT_SCHEDULES",
  "VIEW_SELF_ASSESSMENT_RESULTS",
  "VIEW_SELF_TRAINING_PROGRAM_MODULES",
  "VIEW_SELF_MODULE_QUESTIONS",
  "VIEW_SELF_DEMO_SESSIONS",
  "VIEW_SELF_DEMO_PARTICIPANTS",
];

export const TRAINER_PORTAL_ROUTES = [
  "/batches",
  "/batch-students",
  "/batch-classes",
  "/class-records",
  "/classes-attendance",
  "/attendance-report",
  "/assesments",
  "/assessment-tests",
  "/assessment-schedules",
  "/assesment-results",
  "/training-program-modules",
  "/module-questions",
  "/demo-sessions",
  "/demo-participants",
];

function includesTrainer(value) {
  return String(value || "").toLowerCase().includes("trainer");
}

export function isTrainerPortalUser(userData, permissionList = null) {
  if (!userData || isStudentUser(userData, permissionList)) return false;

  const normalized = Array.isArray(permissionList)
    ? permissionList.map((item) => String(item).trim().toUpperCase())
    : [];
  if (TRAINER_PORTAL_PERMISSIONS.some((key) => normalized.includes(key))) {
    return true;
  }

  if (includesTrainer(resolveUserRoleLabel(userData))) return true;

  const loginuser = Array.isArray(userData.loginuser)
    ? userData.loginuser[0]
    : userData.loginuser;

  return [
    userData?.organizationuser?.UserTypes?.user_type_name,
    userData?.organizationuser?.user_types?.user_type_name,
    userData?.organizationuser?.UserTypes?.user_type_code,
    userData?.user_type_name,
    userData?.user_type,
    loginuser?.user_type_name,
    loginuser?.UserTypes?.user_type_name,
    loginuser?.user_types?.user_type_name,
  ].some(includesTrainer);
}

function isAdminRole(userData) {
  return String(resolveUserRoleLabel(userData) || "").trim().toLowerCase() === "admin";
}

function listHasCode(permissionList, code) {
  if (checkPermission(permissionList, code)) return true;
  const target = String(code || "").trim().toUpperCase();
  if (!target || !Array.isArray(permissionList)) return false;
  return permissionList.some((item) =>
    String(item)
      .split("||")
      .map((part) => part.trim().toUpperCase())
      .includes(target)
  );
}

/** VIEW_BATCHES and Admin see every batch, even when they are also assigned to some. */
export function seesAllBatches(permissionList, userData) {
  return listHasCode(permissionList, "VIEW_BATCHES") || isAdminRole(userData);
}

/** Own batches only when the user does not have the full batch list. */
export function seesOnlyOwnBatches(permissionList, userData) {
  if (seesAllBatches(permissionList, userData)) return false;
  return checkPermission(permissionList, "VIEW_SELF_BATCHES");
}

export function withTrainerPortalPermissions(allowed, userData) {
  let list = Array.isArray(allowed) ? allowed.map((item) => String(item)) : [];
  const keepFullBatchList = seesAllBatches(list, userData);
  if (keepFullBatchList) {
    list = list.filter((item) => item.trim().toUpperCase() !== "VIEW_SELF_BATCHES");
  }
  if (!isTrainerPortalUser(userData, list)) return list;
  const seen = new Set(list.map((item) => item.trim().toUpperCase()));
  TRAINER_PORTAL_PERMISSIONS.forEach((key) => {
    if (key === "VIEW_SELF_BATCHES" && keepFullBatchList) return;
    if (!seen.has(key)) list.push(key);
  });
  return list;
}
